import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Input, message } from "antd";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";

import {
  createContextAnalysisFeedback,
  listContextAnalysisFeedback,
  updateContextAnalysisFeedback,
} from "../../service/analysis";

/**
 * ContextAnalysisFeedbackBar — attorney judgment capture on a document's
 * Context Analysis run. The Context Analysis sibling of BriefFeedbackBar.
 *
 * Unlike the Brief bar (whose parent report probes the endpoint once per
 * job and passes rows down), this bar is self-contained: the Context
 * Analysis tab has no report-level parent, so the bar probes the list
 * endpoint itself. The backend 403s users without the
 * `ai.context_analysis.feedback` capability (limited to Matthew), and a
 * 403 simply keeps the bar hidden — one gate controls API access and UI
 * visibility.
 *
 * The backend snapshots the analysis run (model version, computed-at,
 * full markdown) server-side on create, so a comment keeps meaning even
 * after a manual refresh overwrites the analysis in place. One row per
 * author in practice: we PATCH the latest existing row instead of
 * stacking duplicates.
 */

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafbfc;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BarLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const SavedNote = styled.span`
  font-size: 12px;
  color: #059669;
`;

const RATING_UP = "up";
const RATING_DOWN = "down";
const RATING_NEUTRAL = "neutral";

const ContextAnalysisFeedbackBar = ({ documentId }) => {
  const [allowed, setAllowed] = useState(false);
  const [rating, setRating] = useState(RATING_NEUTRAL);
  const [comment, setComment] = useState("");
  const [correction, setCorrection] = useState("");
  const [rowId, setRowId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Probe once per document. 403 (or any failure) keeps the bar
  // hidden — feedback is an enhancement layer, never an error state.
  const probedDocIdRef = useRef(null);
  useEffect(() => {
    if (!documentId || probedDocIdRef.current === documentId) return;
    probedDocIdRef.current = documentId;

    let cancelled = false;
    setAllowed(false);
    setRating(RATING_NEUTRAL);
    setComment("");
    setCorrection("");
    setRowId(null);
    setFormOpen(false);
    setSavedAt(null);

    Promise.resolve(listContextAnalysisFeedback(documentId))
      .then((response) => {
        if (cancelled || !response || response.status !== 200) return;
        const results = response.data?.results || [];
        // Backend orders newest-first; adopt the latest row so edits
        // PATCH in place instead of stacking duplicates.
        const existing = results[0] || null;
        if (existing) {
          setRating(existing.rating || RATING_NEUTRAL);
          setComment(existing.comment || "");
          setCorrection(existing.correction_text || "");
          setRowId(existing.id ?? null);
        }
        setAllowed(true);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[context-analysis-feedback] probe failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const persist = async (fields) => {
    setIsSaving(true);
    try {
      let response;
      if (rowId != null) {
        response = await updateContextAnalysisFeedback(rowId, fields);
      } else {
        response = await createContextAnalysisFeedback(documentId, {
          rating: fields.rating ?? rating ?? RATING_NEUTRAL,
          comment: fields.comment ?? comment ?? "",
          correction_text: fields.correction_text ?? correction ?? "",
        });
      }
      const row = response?.data || null;
      if (row?.id != null) setRowId(row.id);
      setSavedAt(Date.now());
      return true;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Couldn't save your feedback.";
      // eslint-disable-next-line no-console
      console.error("[context-analysis-feedback] save failed:", err);
      message.error(detail, 6);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleThumb = async (value) => {
    const previous = rating;
    setRating(value);
    const ok = await persist({ rating: value });
    if (!ok) setRating(previous);
  };

  const handleSaveNote = async () => {
    const ok = await persist({
      rating,
      comment,
      correction_text: correction,
    });
    if (ok) {
      setFormOpen(false);
      message.success(
        "Feedback recorded — it's kept with this exact analysis run.",
        4
      );
    }
  };

  if (!documentId || !allowed) return null;

  return (
    <Bar data-testid="context-analysis-feedback-bar">
      <BarRow>
        <BarLabel>Was this analysis right?</BarLabel>
        <Button
          size="small"
          type={rating === RATING_UP ? "primary" : "default"}
          icon={
            <ThumbsUp
              size={14}
              weight={rating === RATING_UP ? "fill" : "regular"}
            />
          }
          onClick={() => handleThumb(RATING_UP)}
          disabled={isSaving}
          data-testid="context-analysis-feedback-thumb-up"
          aria-label="Thumbs up"
        />
        <Button
          size="small"
          danger={rating === RATING_DOWN}
          type={rating === RATING_DOWN ? "primary" : "default"}
          icon={
            <ThumbsDown
              size={14}
              weight={rating === RATING_DOWN ? "fill" : "regular"}
            />
          }
          onClick={() => handleThumb(RATING_DOWN)}
          disabled={isSaving}
          data-testid="context-analysis-feedback-thumb-down"
          aria-label="Thumbs down"
        />
        <Button
          size="small"
          type="link"
          onClick={() => setFormOpen((open) => !open)}
          data-testid="context-analysis-feedback-expand"
        >
          {formOpen
            ? "Hide"
            : comment || correction
            ? "Edit note / correction"
            : "Add note / correction"}
        </Button>
        {savedAt != null && !formOpen && (
          <SavedNote data-testid="context-analysis-feedback-saved">
            Saved
          </SavedNote>
        )}
      </BarRow>
      {formOpen && (
        <>
          <div>
            <FieldLabel htmlFor={`caf-comment-${documentId}`}>
              What was wrong (or right)?
            </FieldLabel>
            <Input.TextArea
              id={`caf-comment-${documentId}`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="e.g. The procedural history cites the rating decision where it should cite the enlistment exam."
              data-testid="context-analysis-feedback-comment"
            />
          </div>
          <div>
            <FieldLabel htmlFor={`caf-correction-${documentId}`}>
              What should it have said? (optional — trains future analyses)
            </FieldLabel>
            <Input.TextArea
              id={`caf-correction-${documentId}`}
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 10 }}
              placeholder="Rewrite the analysis (or the part of it) the way an expert attorney would have written it."
              data-testid="context-analysis-feedback-correction"
            />
          </div>
          <BarRow>
            <Button
              size="small"
              type="primary"
              onClick={handleSaveNote}
              loading={isSaving}
              data-testid="context-analysis-feedback-save"
            >
              Save feedback
            </Button>
            <Button
              size="small"
              onClick={() => setFormOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </BarRow>
        </>
      )}
    </Bar>
  );
};

ContextAnalysisFeedbackBar.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ContextAnalysisFeedbackBar.defaultProps = {
  documentId: null,
};

export default ContextAnalysisFeedbackBar;
